import { defineElement, FC, useElement, useListen, useProp } from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { Modal } from '@source/components/molecules/modal';
import { elements } from '@source/utilities/dom';
import { useStylesheet } from '@utilities/hooks';

import { ModalWrapper } from '../modal-wrapper/modal-wrapper';

const CONTENT_CONTAINER_HOOK = '[js-hook-content-page]';

const ContentFetcher: FC = () => {
    const [, setHasActiveModal] = useProp<boolean>('hasActiveModal', false);
    const [cachedModal, setCachedModal] = useProp<Modal>('cachedModal', {} as Modal);
    const [modalId, setModalId] = useProp<string>('modalId', '');
    const link = useElement<HTMLAnchorElement>('a');

    useStylesheet('content-fetcher {display: block;}');
    useListen(link, 'click', (e) => {
        e.preventDefault();

        const { href } = e.currentTarget as HTMLAnchorElement;

        setModalId(href);
        fetchContent(href);
    });

    /**
     * Fetches the page of the Anchor element.
     * @private
     * @param {string} url
     * @memberof ContentFetcher
     */
    function fetchContent(url: string) {
        // Check if we have a cached modal otherwise create one.
        if (cachedModal) {
            cachedModal.active = true;
        } else {
            fetch(url)
                .then((response) => response.text())
                .then((response) => createModal(response))
                .catch(() => setHasActiveModal(false));
        }
    }

    /**
     * Creates a BPD modal and inserts the container of the fetched page.
     * @private
     * @param {string} text
     * @memberof ContentFetcher
     */
    async function createModal(text: string) {
        const html = stringToHTML(text);
        const container = html.querySelector(CONTENT_CONTAINER_HOOK);

        if (!container) return;
        // Create modal
        const modal = renderModal(container);

        // Cache modal
        setCachedModal(modal);
        elements.body.appendChild(modal);
    }

    function renderModal(children: Node) {
        return (
            <ModalWrapper id={modalId} active={true}>
                {children}
            </ModalWrapper>
        );
    }

    /***
     * Transforms string into a node element.
     * @private
     * @param {string} text
     * @returns {Node}
     * @memberof ContentFetcher
     */
    function stringToHTML(text: string) {
        const template = document.createElement('div');

        text = text.trim();

        template.innerHTML = text;

        return template;
    }
};

ContentFetcher.props = {
    hasActiveModal: {
        type: Boolean,
    },
    cachedModal: {
        type: Modal,
    },
    modalId: {
        type: String,
    },
};

defineElement('content-fetcher', ContentFetcher);
